import * as React from 'react';
import type { MoveKind } from '@chess/api-model';
import type { HelpTopic } from '@chess/api-help';
import { DECISIVE_ZONE_PAWNS } from '@chess/api-mistakes';
import { KIND_WORDS, MAX_FAMILY_ROWS } from './cards.ts';

// The help topics behind the Blunders and Misses cards' "?", one per card and
// kind (`mistakes.<kind>.<card>`), each with the rule its numbers follow and
// the paragraph on what a mistake costs. Registered by this plugin's setup.

export type CardId = 'patterns' | 'severity' | 'standing' | 'clock' | 'material' | 'weeks';
export const CARD_IDS = ['patterns', 'severity', 'standing', 'clock', 'material', 'weeks'] as const satisfies readonly CardId[];

export const helpId = (kind: MoveKind, card: CardId) => `mistakes.${kind}.${card}`;

const Cost: React.FC<{ kind: MoveKind }> = ({ kind }) => (
  <>
    <h3>What a {KIND_WORDS[kind].one} costs</h3>
    <p>
      {kind === 'blunder'
        ? 'A blunder costs the pawns your score dropped by the move: the score before it (with the best move) less the score after it.'
        : "A miss costs the pawns you passed over: the score the engine's best move would have kept less the score after the move you played."}{' '}
      Only the part of the drop between −{DECISIVE_ZONE_PAWNS} and +{DECISIVE_ZONE_PAWNS} pawns counts, where games are decided: a score past ±{DECISIVE_ZONE_PAWNS} is
      held at {DECISIVE_ZONE_PAWNS}. So going from +10 to +4 costs nothing (you are still winning), going from +1 to −5 costs 4, and a forced mate costs at most{' '}
      {2 * DECISIVE_ZONE_PAWNS}.
    </p>
  </>
);

const Games: React.FC<{ kind: MoveKind }> = ({ kind }) => (
  <p>
    The cards count <b>games</b>: a game counts once in a bar when it has at least one such {KIND_WORDS[kind].one}, so the number is the count of games the bar opens in
    the library. Only your scanned games of the last 30 days count.
  </p>
);

const BODY: Record<CardId, (kind: MoveKind) => React.ReactNode> = {
  patterns: (kind) => (
    <>
      <p>
        Each bar is a pattern of {KIND_WORDS[kind].many}, as long as the pawns they {KIND_WORDS[kind].costVerb} in total; the costliest first, the games at the right. A
        pattern is named like an opening: a <b>family</b> (pin, skewer, fork, discovered attack, hanging piece, trapped piece, capture the defender, mate, pawn, king
        attack, combination) and a <b>variation</b> within it (a pin against the queen, a fork by a knight, a back-rank mate). The bars are the <b>worst {MAX_FAMILY_ROWS}</b>{' '}
        families; the note says when others are left out, and the table's chips still hold every family. A chip opens a family's variations, and the Pattern column names both.
      </p>
      <p>
        {kind === 'blunder'
          ? "A blunder's pattern is the tactic the opponent gets after your move, read from the engine's best reply; Positional when no tactic explains the loss."
          : "A miss's pattern is the tactic you could have played, read from the engine's best line; Positional when a clearly better line won no material."}{' '}
        Families with no {KIND_WORDS[kind].one} are left out.
      </p>
    </>
  ),
  severity: () => (
    <p>
      How much of your chance of winning the move gave up, on the expected-score curve: <b>Decisive</b> 30 points or more, <b>Serious</b> 15 or more, <b>Minor</b> less.
      So +8 to +5 is minor, and +1 to −1 is not.
    </p>
  ),
  standing: (kind) => (
    <p>
      How you stood just before the {KIND_WORDS[kind].one}, by your Winning setting: <b>Ahead</b> at it or more, <b>Behind</b> at minus it or less, <b>Level</b> between. The
      bar is the games, the figure at the right the pawns those {KIND_WORDS[kind].many} {KIND_WORDS[kind].costVerb}. Do you relax when ahead, or gamble when behind?
    </p>
  ),
  clock: (kind) => (
    <p>
      The time left on your clock just before the {KIND_WORDS[kind].one}: under a minute, one to three minutes, three or more. Games whose record has no clock times are left
      out; the note says how many.
    </p>
  ),
  material: (kind) => (
    <p>
      {kind === 'blunder'
        ? "What the opponent's best reply wins, net: a piece, a pawn or a forced mate."
        : 'What your best move would have won, net: a piece, a pawn or a forced mate.'}{' '}
      Those that {kind === 'blunder' ? 'lost' : 'missed'} no material are the positional ones, on the first card.
    </p>
  ),
  weeks: (kind) => (
    <p>
      Your {KIND_WORDS[kind].many} per scanned game in each week of the last 30 days, oldest at the left; the oldest column takes the two days left over. A column opens that
      week's games with a {KIND_WORDS[kind].one}.
    </p>
  ),
};

const TITLE: Record<CardId, (kind: MoveKind) => string> = {
  patterns: (kind) => KIND_WORDS[kind].patternsTitle,
  severity: () => 'How costly',
  standing: () => 'Where you stood',
  clock: () => 'On the clock',
  material: (kind) => KIND_WORDS[kind].materialTitle,
  weeks: () => 'Week by week',
};

/** A card's title, within its kind. */
export const cardTitle = (kind: MoveKind, card: CardId) => TITLE[card](kind);

export function cardHelp(kind: MoveKind, card: CardId): HelpTopic {
  return {
    title: cardTitle(kind, card),
    body: (
      <>
        {BODY[card](kind)}
        {card !== 'weeks' && <Games kind={kind} />}
        <Cost kind={kind} />
      </>
    ),
  };
}
